import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditProviderAttachmentModal from '@modals/CreateOrEditProviderAttachmentModal'
import ManageProviderOrderItemStatusModal from '@modals/ManageProviderOrderItemStatusModal'
import MediaReviewerModal from '@modals/MediaReviewerModal'
import ShareProjectModal from '@modals/ShareProjectModal'

import type { CampaignModel, ImageModel, ProviderModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditProviderAttachmentModal: false,
  showManageProviderOrderItemStatusModal: false,
  showMediaReviewerModal: false,
  showShareProjectModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditProviderAttachmentModal: {
      closeModal: () => setState({ showCreateOrEditProviderAttachmentModal: false }),
      updateAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageProviderOrderItemStatusModal: {
      closeModal: () => setState({ showManageProviderOrderItemStatusModal: false }),
      updateProviderOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    MediaReviewerModal: {
      closeModal: () => setState({ showMediaReviewerModal: false }),
    },
    ShareProjectModal: {
      closeModal: () => setState({ showShareProjectModal: false }),
      shareProject: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditProviderAttachmentModal,
    showManageProviderOrderItemStatusModal,
    showMediaReviewerModal,
    showShareProjectModal,
  } = state

  const { campaignId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditProviderAttachmentModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditProviderAttachmentModal', payload)
        setState({ showCreateOrEditProviderAttachmentModal: true })
      },
      showManageProviderOrderItemStatusModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('ManageProviderOrderItemStatusModal', payload)
        setState({ showManageProviderOrderItemStatusModal: true })
      },
      showMediaReviewerModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('MediaReviewerModal', payload)
        setState({ showMediaReviewerModal: true })
      },
      showShareProjectModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('ShareProjectModal', payload)
        setState({ showShareProjectModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent campaignId={campaignId} />

        <CreateOrEditProviderAttachmentModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditProviderAttachmentModal', setState)}
          showModal={showCreateOrEditProviderAttachmentModal}
        />

        <ManageProviderOrderItemStatusModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageProviderOrderItemStatusModal', setState)}
          showModal={showManageProviderOrderItemStatusModal}
        />

        <MediaReviewerModal
          callbacks={callbacks('MediaReviewerModal', setState)}
          showModal={showMediaReviewerModal}
        />

        <ShareProjectModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ShareProjectModal', setState)}
          showModal={showShareProjectModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
